/** @jsx jsx */
import React, { useState, useEffect } from "react";
import { jsx } from "@emotion/core";
import { Formik, FieldArray } from "formik";
import { Redirect } from "react-router";
import _last from "lodash/last";
import _find from "lodash/find";
import _get from "lodash/get";

import { resourceUrls } from "../../../../config";

import { get, post } from "../../../api";

import PrimaryCTA from "../../../../components/PrimaryCTA";
import PrimaryCTAButton from "../../../../components/PrimaryCTA";

import Form from "../../../../components/Form";
import {
    FormikGradePicker,
    FormikSubjectPicker,
} from "../../../../components/Input";
import RemoveButton from "../../../../components/RemoveButton";
import HorizontalDivider from "../../../../components/HorizontalDivider";
import { handleGet, handlePost } from "../../../api/handler";
import PageContainer from "../../../components/PageContainer";
import { useModal } from "../../../../components/Modal/useModal";
import { submitGradesInterceptorModal } from '../../../index';
import { BackButton } from "../../../../components/BackButton";

const initialValues = {
    subjects: [
        {
            subject: null,
            grade: null,
        },
    ],
};

const fetchSubjects = async ({
    setIsLoading,
    setIsLoggedIn,
    setError,
    setSubjects,
}) => {
    await handleGet({
        getApiCall: () => get({ url: resourceUrls.qualifications.getSubjects }),
        setIsLoading: setIsLoading,
        setIsLoggedIn: setIsLoggedIn,
        setError: setError,
        setData: setSubjects,
    });
};

const fetchGrades = async ({
    setIsLoading,
    setIsLoggedIn,
    setError,
    setGrades,
    qualificationKey,
}) => {
    await handleGet({
        getApiCall: () =>
            get({
                url: resourceUrls.qualifications.getGrades(qualificationKey),
            }),
        setIsLoading: setIsLoading,
        setIsLoggedIn: setIsLoggedIn,
        setError: setError,
        setData: setGrades,
    });
};

const submitQualifications = (
    setIsLoggedIn,
    setError,
    setFormSubmissionComplete,
    qualificationType
) => async (values, actions) => {
    const tutorQualifications = _get(values, "subjects").map(qualification => ({
        subject: _get(qualification, "subject.label"),
        grade: _get(qualification, "grade.label"),
        qualification: qualificationType,
    }));

    const body = {
        applicationStep: "SCHOOL_QUALIFICATION",
        tutorQualifications,
    };

    await handlePost({
        postApiCall: () =>
            post({ url: resourceUrls.qualifications.submit, body: body }),
        setIsLoggedIn: setIsLoggedIn,
        setError: setError,
        setIsSubmissionComplete: setFormSubmissionComplete,
    });

    actions.setSubmitting(false);
};

const Subjects = ({ qualificationType, qualificationTypeOptions }) => {

    const [grades, setGrades] = useState([]);
    const [subjects, setSubjects] = useState([]);

    const [isLoading, setIsLoading] = useState(true);
    const [isLoggedIn, setIsLoggedIn] = useState(false);
    const [error, setError] = useState(null);
    const [formSubmissionComplete, setFormSubmissionComplete] = useState(false);

    const { openModal } = useModal();

    const qualificationKey = _get(
        _find(qualificationTypeOptions, ["key", qualificationType]),
        "key"
    );

    useEffect(() => {
        fetchSubjects({ setIsLoading, setIsLoggedIn, setError, setSubjects });
    }, []);

    useEffect(() => {
        fetchGrades({
            setIsLoading,
            setIsLoggedIn,
            setError,
            setGrades,
            qualificationKey,
        });
    }, []);

    if (formSubmissionComplete) {
        return (
            <Redirect
                push
                to={{
                    pathname: "/secure/apply",
                }}
            />
        );
    }

    return (
        <>
            <PageContainer
                isLoading={isLoading}
                error={error}
                isLoggedIn={isLoggedIn}
            >
                <h2>
                    Which subjects did you take at{" "}
                    {_get(
                        _find(qualificationTypeOptions, [
                            "key",
                            qualificationType,
                        ]),
                        "label",
                        "your level"
                    )}
                    ?
                </h2>
                <p>
                    We take all of your grades into account for your
                    application, so please add as many as you have.
                </p>
                <Formik
                    initialValues={initialValues}
                    onSubmit={submitQualifications(
                        setIsLoggedIn,
                        setError,
                        setFormSubmissionComplete,
                        qualificationType
                    )}
                    render={({ isSubmitting, ...props}) => (
                        <Form onSubmit={props.handleSubmit}>
                            <FieldArray
                                name="subjects"
                                render={arrayHelpers => (
                                    <>
                                        {props.values.subjects.map(
                                            (subject, index) => (
                                                <>
                                                    <FormikSubjectPicker
                                                        subjects={subjects}
                                                        index={index}
                                                        touched={props.touched}
                                                        errors={props.errors}
                                                    />
                                                    <FormikGradePicker
                                                        freeText={
                                                            qualificationType ===
                                                            "OTHER_MAIN"
                                                        }
                                                        grades={grades}
                                                        index={index}
                                                        touched={props.touched}
                                                        errors={props.errors}
                                                        handleBlur={
                                                            props.handleBlur
                                                        }
                                                    />
                                                    {index > 0 && (
                                                        <RemoveButton
                                                            onClick={e => {
                                                                e.preventDefault();
                                                                arrayHelpers.remove(
                                                                    index
                                                                );
                                                                // required to stop Formik validating
                                                                // untouched subjects
                                                                return props.setFieldTouched(
                                                                    "subjects",
                                                                    false
                                                                );
                                                            }}
                                                        >
                                                            Remove subject
                                                        </RemoveButton>
                                                    )}
                                                    <HorizontalDivider />
                                                </>
                                            )
                                        )}
                                        <PrimaryCTA
                                            type="button"
                                            disabled={
                                                _get(
                                                    _last(
                                                        props.values.subjects
                                                    ),
                                                    "subject"
                                                ) === null ||
                                                _get(
                                                    _last(
                                                        props.values.subjects
                                                    ),
                                                    "grade"
                                                ) === null
                                            }
                                            onClick={e => {
                                                e.preventDefault();
                                                arrayHelpers.push({
                                                    subject: null,
                                                    grade: null,
                                                });
                                                // required to stop Formik validating the new subject
                                                // as soon as it's added
                                                return props.setFieldTouched(
                                                    "subjects",
                                                    false
                                                );
                                            }}
                                        >
                                            Add another
                                        </PrimaryCTA>
                                    </>
                                )}
                            />

                            <PrimaryCTAButton  
                                data-testid="school-leaving-subjects-next-button" 
                                type="button"
                                disabled={isSubmitting}
                                onClick={async () => {
                                const validation = await props.validateForm();
                                if (validation.subjects) {
                                    // Programmatically submit the form to display the errors.
                                    return props.submitForm();
                                }
                                openModal(submitGradesInterceptorModal(props))
                            }}>
                                Next →
                            </PrimaryCTAButton>
                        </Form>
                    )}
                />
            </PageContainer>
        </>
    );
};

export default Subjects;
